/**
 * What the cashier reads / shows to the 免税 buyer (T07-30, design/05 §3.3) — in the buyer's
 * language (en / ja / th / zh-Hant / zh-Hans), not the staff member's. The keys are tax-service's
 * `noticeKeys` (`taxfree.notice.*` — `services/tax/app/services/taxfree_eligibility.py`).
 *
 * ⚠ Wording to be confirmed by the 税理士 (T07-39) before the shop sells 免税; facts from
 * docs/research/japan-tax-legal.md (2026-11-01 リファンド方式: customs within 90 days, the shop
 * refunds the tax after the customs check).
 */

export const BUYER_LOCALES = ["en", "ja", "zh-Hant", "zh-Hans", "th"] as const;
export type BuyerLocale = (typeof BUYER_LOCALES)[number];

export const BUYER_LOCALE_LABEL: Record<BuyerLocale, string> = {
  en: "English",
  ja: "日本語",
  "zh-Hant": "繁體中文",
  "zh-Hans": "简体中文",
  th: "ไทย",
};

type Texts = Record<BuyerLocale, string>;

export const NOTICE_TEXTS: Record<string, Texts> = {
  must_export: {
    en: "These goods must be taken out of Japan. Do not use or give them away in Japan.",
    ja: "お買い上げの品は日本国外へ持ち出してください。国内で使用・譲渡しないでください。",
    "zh-Hant": "所購商品必須攜出日本，請勿在日本國內使用或轉讓。",
    "zh-Hans": "所购商品必须带出日本，请勿在日本国内使用或转让。",
    th: "สินค้าเหล่านี้ต้องนำออกนอกประเทศญี่ปุ่น ห้ามใช้หรือมอบให้ผู้อื่นในญี่ปุ่น",
  },
  show_passport_at_departure: {
    en: "Show your passport and these goods to Customs when you leave Japan.",
    ja: "出国時に税関でパスポートとお買い上げ品をご提示ください。",
    "zh-Hant": "出境時請向海關出示護照及所購商品。",
    "zh-Hans": "出境时请向海关出示护照及所购商品。",
    th: "เมื่อออกจากญี่ปุ่น กรุณาแสดงหนังสือเดินทางและสินค้าต่อศุลกากร",
  },
  customs_within_90_days: {
    en: "Pass Japanese Customs with these goods within 90 days of purchase.",
    ja: "ご購入日から90日以内に、お買い上げ品とともに税関を通過してください。",
    "zh-Hant": "請於購買日起90天內攜帶所購商品通過日本海關。",
    "zh-Hans": "请于购买日起90天内携带所购商品通过日本海关。",
    th: "กรุณาผ่านศุลกากรญี่ปุ่นพร้อมสินค้าภายใน 90 วันนับจากวันที่ซื้อ",
  },
  refund_after_customs: {
    en: "You pay the price with tax today. After Customs confirms the export, this shop pays the tax back to you.",
    ja: "本日は税込でお支払いいただき、税関で輸出が確認された後に当店から消費税をお返しします。",
    "zh-Hant": "今天以含稅價格付款，海關確認出口後，本店將退還消費稅。",
    "zh-Hans": "今天以含税价格付款，海关确认出口后，本店将退还消费税。",
    th: "วันนี้ชำระราคารวมภาษี เมื่อศุลกากรยืนยันการส่งออกแล้ว ร้านจะคืนภาษีให้",
  },
  no_tax_at_counter: {
    en: "Consumption tax is not charged on these goods today.",
    ja: "本日のお買い上げ品には消費税がかかりません。",
    "zh-Hant": "本次購買商品不收取消費稅。",
    "zh-Hans": "本次购买商品不收取消费税。",
    th: "สินค้าที่ซื้อวันนี้ไม่ต้องเสียภาษีการบริโภค",
  },
  consumable_sealed: {
    en: "Consumables are sealed in a special bag. Do not open it before leaving Japan.",
    ja: "消耗品は専用の袋に封入しています。出国まで開封しないでください。",
    "zh-Hant": "消耗品已封入專用袋，出境前請勿開封。",
    "zh-Hans": "消耗品已封入专用袋，出境前请勿开封。",
    th: "สินค้าสิ้นเปลืองถูกปิดผนึกในถุงพิเศษ ห้ามเปิดก่อนออกจากญี่ปุ่น",
  },
  high_value_detail: {
    en: "Items of ¥1,000,000 or more are recorded with their serial number.",
    ja: "100万円以上の商品は、シリアル番号とともに記録します。",
    "zh-Hant": "100萬日圓以上的商品將連同序號一併記錄。",
    "zh-Hans": "100万日元以上的商品将连同序列号一并记录。",
    th: "สินค้าตั้งแต่ ¥1,000,000 ขึ้นไปจะบันทึกพร้อมหมายเลขซีเรียล",
  },
};

export const REGIME_TEXTS: Record<"exemption" | "refund", Texts> = {
  exemption: {
    en: "Tax-free price today: {{amount}} (no consumption tax).",
    ja: "本日は免税価格 {{amount}}（消費税なし）でお買い上げいただけます。",
    "zh-Hant": "今天以免稅價格 {{amount}}（不含消費稅）購買。",
    "zh-Hans": "今天以免税价格 {{amount}}（不含消费税）购买。",
    th: "วันนี้ซื้อในราคาปลอดภาษี {{amount}} (ไม่มีภาษีการบริโภค)",
  },
  refund: {
    en: "Today you pay {{total}} including tax. After Customs checks the goods, we refund {{refund}}.",
    ja: "本日は税込 {{total}} をお支払いいただき、税関確認後に当店から {{refund}} をお返しします。",
    "zh-Hant": "今天支付含稅 {{total}}，海關確認後本店退還 {{refund}}。",
    "zh-Hans": "今天支付含税 {{total}}，海关确认后本店退还 {{refund}}。",
    th: "วันนี้ชำระ {{total}} รวมภาษี หลังศุลกากรตรวจแล้ว ร้านจะคืน {{refund}}",
  },
};

export function noticeCode(key: string): string {
  return key.replace(/^taxfree\.notice\./, "");
}

export function fill(template: string, values: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => values[name] ?? "");
}
